import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";

import useLanguageChange from "../../hooks/useLanguageChange";

import { CALL_DATA, GLOBAL_MEETING_DATA, PublicVariables } from "../../constants/PublicVariables";
import { CALL_BODY_SCREEN, DEVICE_TYPES, EXTRACT_TEXT_VIEW } from "../../constants/Constants";

import { LogFirebase } from "../../services/firestore";
import { Fancybox } from "../../shared/FancyboxJS";

import DeleteImageFromContainer from "./DeleteImageFromContainer";
import { setIsLiveLocationMapOpen } from "../../reducers/live-location-reducer";
import useBottomMenuClass from "../../hooks/useBottomMenuClass";

const ImageContainerDrawer = ({ showContainerView = false }) => {
    const dispatch = useDispatch();
    const fancyRef = useRef();
    const ldFlags = useFlags();

    const [deleteImgObj, setDeleteImgObj] = useState(null);
    const [showContainer, setShowContainer] = useState(showContainerView);
    const [displayContainer, setDisplayContainer] = useState(false);

    const { containerImagesArray } = useSelector((state) => state.imageContainerReducer);
    const { extractTextInprogress } = useSelector((state) => state.extractText);
    const { currentCallBodyScreen } = useSelector((state) => state.commonReducer);
    const { isLiveLocationMapOpen } = useSelector((state) => state.liveLocation);

    useBottomMenuClass(showContainer);

    const storageType = useMemo(() => {
        return { aws: "2", zendesk: "3" };
    }, []);

    /* custome hook for getting updated localized value object for entire project 
    This will give localize value object for selected language
    Also will give updated localized value object when language change */
    const localisedValues = useLanguageChange();

    let facyBoxButtons = useMemo(() => {
        if (PublicVariables.isIOSDevice || CALL_DATA.storageType === storageType.aws || CALL_DATA.storageType === storageType.zendesk) {
            // if ios device, aws & zendesk then hide download option
            return ["slideshow", "fullscreen", "close"];
        } else {
            return ["slideshow", "fullscreen", "download", "close"];
        }
    }, []);

    useEffect(() => {
        return () => {
            setShowContainer(false);
            document.body.classList.remove("open-saved-image-sidebar");
            setDisplayContainer(false);
        };
    }, []);

    useEffect(() => {
        if (showContainer && isLiveLocationMapOpen) {
            dispatch(setIsLiveLocationMapOpen(false));
        }
    }, [showContainer]);

    useEffect(() => {
        if (!PublicVariables.isOnlyUploadImageMeet) {
            const uploadFileScreens = [CALL_BODY_SCREEN.UPLOAD_FILE_SCREEN, CALL_BODY_SCREEN.NOTIFY_SCREEN];
            setDisplayContainer(!uploadFileScreens.includes(currentCallBodyScreen));
        }
    }, [currentCallBodyScreen]);

    useEffect(() => {
        if (!PublicVariables.isOnlyUploadImageMeet) {
            // add condition here for !callannview && !callocrview
            if (
                containerImagesArray.length == 1 &&
                extractTextInprogress !== EXTRACT_TEXT_VIEW.OCR_VIEW &&
                extractTextInprogress !== EXTRACT_TEXT_VIEW.PREPARE_VIEW &&
                GLOBAL_MEETING_DATA.device_type == DEVICE_TYPES.DESKTOP
            ) {
                if (!showContainer) toggleContainerView();
            }

            setDisplayContainer(containerImagesArray.length > 0);
        }
    }, [containerImagesArray]);

    const toggleContainerView = () => {
        setShowContainer((prevState) => {
            if (!prevState) {
                document.body.classList.add("open-saved-image-sidebar");
            } else {
                document.body.classList.remove("open-saved-image-sidebar");
            }
            return !prevState;
        });
    };

    const closeDeleteModal = () => {
        setDeleteImgObj(null);
        console.log("containerImagesArray.length", containerImagesArray.length);
        LogFirebase("DeleteImage", "TotalPhotosAfterDelete", containerImagesArray.length);
    };

    const openDeleteModal = (imageObj) => {
        if (imageObj && !imageObj.name) {
            console.log("Error: Invalid input. Image name required.");
            return false;
        }
        setDeleteImgObj(imageObj);
        LogFirebase("DeleteImage", "DeleteClicked", { imageName: imageObj.name });
    };

    return (
        <>
            <div className={clsx("sidebar right-sidebar saved-image-sidebar", !displayContainer && "d-none", showContainer && "active")}>
                <div className='capture-image-count'>
                    <button className='btn btn-primary capture-image-count-btn' id='image-container-toggle-btn' onClick={toggleContainerView}>
                        <span className='btn-icon icon-left-arrow'></span>
                        <span className='btn-text'>{containerImagesArray?.length}</span>
                    </button>
                </div>
                <div className='sidebar-in'>
                    <div className='sidebar-head'>
                        <div className='sidebar-head-title'>
                            <div className='sidebar-head-icon'>
                                <span className='for-icon icon-image'></span>
                            </div>
                            <div className='title'>{localisedValues?.saved_images}</div>
                        </div>
                        <div className='sidebar-head-right'>
                            <div className='sidebar-close'>
                                <button className='btn' onClick={toggleContainerView}>
                                    <span className='for-icon icon-close-light'></span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className='sidebar-body custom-scroll'>
                        <div className='saved-image-list'>
                            <Fancybox
                                fancyRef={fancyRef}
                                options={{
                                    Hash: false,
                                    Carousel: {
                                        infinite: false,
                                    },
                                    Toolbar: {
                                        display: {
                                            left: ["infobar"],
                                            middle: [],
                                            right: facyBoxButtons,
                                        },
                                    },
                                    Thumbs: false,
                                }}
                            >
                                <>
                                    {containerImagesArray?.map((image, index) => {
                                        return (
                                            <div className='saved-image-col' key={`Image-${image.name}-${index}`}>
                                                <div className='saved-image-box'>
                                                    <div className='saved-image-top'>
                                                        {image.original && (
                                                            <a
                                                                className='open-saved-image'
                                                                key={`Fancy-Image-${image.name}`}
                                                                data-fancybox
                                                                data-caption={image.caption}
                                                                href={image.original}
                                                                rel='noreferrer'
                                                            >
                                                                <div className='view-button'>
                                                                    <div className='view-btn'>
                                                                        <span className='for-text'>{localisedValues?.view_text}</span>
                                                                    </div>
                                                                </div>
                                                                <div className='saved-image-wrap'>
                                                                    <img className='saved-image-img' src={image.thumb} alt='Capture with view' />
                                                                </div>
                                                            </a>
                                                        )}
                                                        {!image.original && (
                                                            <div className='open-saved-image' key={`Non-Fancy-Image-${image.name}`}>
                                                                <div className='view-button'>
                                                                    <div className='view-btn'>
                                                                        <span className='for-text'>{localisedValues?.view_unavailable}</span>
                                                                    </div>
                                                                </div>
                                                                <div className='saved-image-wrap'>
                                                                    <img className='saved-image-img' src={image.thumb} alt='Capture without view' />
                                                                </div>
                                                            </div>
                                                        )}
                                                        {image.name && ldFlags.deleteImageDuringCall && (
                                                            <div className='delete-image custom-tooltip tooltip-auto-width m-hide'>
                                                                <button className='btn delete-btn' onClick={() => openDeleteModal(image)}>
                                                                    <span className='btn-icon icon-close-light'></span>
                                                                </button>
                                                                <div className='custom-tooltip-content text-nowrap'>
                                                                    <div className='custom-tooltip-in'>
                                                                        <p>{localisedValues?.delete_lable}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className='saved-image-info'>
                                                        <div className='saved-image-name'>
                                                            {image.original && (
                                                                <div className='saved-image-icon'>
                                                                    <span className='for-icon icon-image'></span>
                                                                </div>
                                                            )}
                                                            <div className='name'>
                                                                {image.isLocationImage
                                                                    ? localisedValues?.location_image_thumb_caption
                                                                    : localisedValues?.image_thumb_caption}
                                                                &nbsp;{containerImagesArray?.length - index}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </>
                            </Fancybox>
                        </div>
                    </div>
                </div>
            </div>
            {deleteImgObj && <DeleteImageFromContainer deleteImgObj={deleteImgObj} closeDeleteModal={closeDeleteModal} />}
        </>
    );
};

ImageContainerDrawer.propTypes = {
    showContainerView: PropTypes.bool,
};

export default ImageContainerDrawer;
